import { ErrorMessage, Input as BronsonInput } from "@vwfs-bronson/bronson-react";
import { DATE_FORMAT, DATE_PLACEHOLDER, DEFAULT_DATE } from "@common/date";
import dayjs from "dayjs";
import { useField } from "formik";
import React, { useCallback } from "react";
import { Label } from "./Label";
import { FormField } from "@common/types";

interface Props {
  formField: FormField;
  label: string;
  infoIconText?: React.ReactNode;
  autocomplete?: string;
  required?: boolean;
}

const DATE_INPUT_PATTERN = /[\d_]{2}\.[\d_]{2}\.[\d_]{4}/;

const isTouchDevice = (): boolean => {
  return window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
};

const replaceAt = (value: string, index: number, char: string): string => {
  const array = value.split("");
  array[index] = char;
  return array.join("");
};

const interpretUserInput = (input: { key: string; caretPosition: number; value: string }): { value: string; caretPosition: number } => {
  const { key, caretPosition, value } = input;
  const unchanged = {
    value,
    caretPosition,
  };

  if (!DATE_INPUT_PATTERN.test(value)) {
    return unchanged;
  }

  const isNumeric = /^\d$/.test(key);

  switch (caretPosition) {
    case 0:
    case 3:
      return isNumeric
        ? {
            value: replaceAt(value, caretPosition, key),
            caretPosition: caretPosition + 1,
          }
        : unchanged;
    case 1:
    case 4:
      return isNumeric
        ? {
            value: replaceAt(value, caretPosition, key),
            caretPosition: caretPosition + 2,
          }
        : unchanged;
    case 2:
    case 5:
      if (isNumeric) {
        return {
          value: replaceAt(value, caretPosition + 1, key),
          caretPosition: caretPosition + 2,
        };
      } else {
        return {
          value,
          caretPosition: caretPosition + 1,
        };
      }
    case 6:
    case 7:
    case 8:
    case 9:
      return isNumeric
        ? {
            value: replaceAt(value, caretPosition, key),
            caretPosition: caretPosition + 1,
          }
        : unchanged;
    default:
      return {
        value,
        caretPosition,
      };
  }
};

/**
 * Tries to parse the input string as a german date and returns the value in the provided format or the input string if not.
 * @param input string
 * @returns A re-formatted date, or the input string
 */
const formatIfValid = (input: string, outputFormat: string = DATE_FORMAT): string => {
  const date = dayjs(input, "DD.MM.YYYY", true);
  return date.isValid() ? date.format(outputFormat) : input;
};

/**
 * Tries to parse the given string as date in DATE_FORMAT and returns a german date, the value itself or a DEFAULT_VALUE
 *
 * @param input
 * @returns A date in german date format, the input string or a default value
 */
const asDisplayValue = (input: string): string => {
  const dateValue = dayjs(input, DATE_FORMAT, true);
  return dateValue.isValid() ? dateValue.format("DD.MM.YYYY") : input || "";
};

export const TextDatePicker = ({ formField, label, infoIconText, autocomplete, required = false }: Props) => {
  const { fieldName } = formField;
  const [fieldProps, { touched, error }, { setValue, setTouched }] = useField<string>(fieldName);

  const errorMessage = touched && error ? <ErrorMessage id={fieldName}>{error}</ErrorMessage> : null;

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;

    if (!["Backspace", "Delete"].includes(event.key)) {
      return;
    }

    event.preventDefault();

    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? 0;
    const isRangeSelect = selectionStart !== selectionEnd;

    if (isRangeSelect) {
      for (let c = selectionEnd; c > selectionStart; c--) {
        target.value = replaceAt(target.value, c - 1, DEFAULT_DATE.split("")[c - 1]);
      }

      target.selectionStart = target.selectionEnd = Math.max(0, Math.min(selectionStart, selectionEnd - 1));
      return;
    }

    switch (event.key) {
      case "Backspace": {
        target.value = replaceAt(target.value, selectionStart - 1, DEFAULT_DATE.split("")[selectionStart - 1]);
        target.selectionStart = target.selectionEnd = Math.max(0, Math.min(selectionStart, selectionEnd - 1));
        break;
      }
      case "Delete": {
        target.value = replaceAt(target.value, selectionStart, DEFAULT_DATE.split("")[selectionStart]);
        target.selectionStart = target.selectionEnd = selectionStart + 1;
        break;
      }
    }
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      const target = event.target as HTMLInputElement;

      if (event.key === "Enter") {
        return;
      }

      event.preventDefault();

      const { value, caretPosition } = interpretUserInput({
        value: target.value,
        caretPosition: target.selectionStart!,
        key: event.key,
      });

      target.value = value;
      target.selectionStart = target.selectionEnd = caretPosition;

      setValue(formatIfValid(target.value));
    },
    [setValue]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>): void => {
      event.preventDefault();

      const clipboardValue = event.clipboardData.getData("text");

      const outcome = clipboardValue
        .replace(/[^\d]/g, "")
        .split("")
        .reduce(
          (acc, v) => {
            return interpretUserInput({
              value: acc.value,
              caretPosition: acc.caretPosition ?? 0,
              key: v,
            });
          },
          {
            value: event.currentTarget.value,
            caretPosition: event.currentTarget.selectionStart,
          }
        );

      const target = event.currentTarget;
      target.value = outcome.value;
      target.selectionStart = target.selectionEnd = outcome.caretPosition;

      setValue(outcome.value);
    },
    [setValue]
  );

  return (
    <>
      <Label htmlFor={fieldName} text={label} infoIconText={infoIconText} />
      <BronsonInput
        aria-describedby={`error-${fieldName}`}
        id={fieldName}
        name={fieldName}
        value={asDisplayValue(fieldProps.value)}
        error={!!errorMessage}
        autoComplete={autocomplete || "off"}
        inputMode="numeric"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setValue(formatIfValid(event.target.value));
        }}
        onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
          setTouched(true);
          if (event.target.value === DEFAULT_DATE) {
            setValue("", true);
          } else {
            setValue(formatIfValid(event.target.value), true);
          }
        }}
        onFocus={async ({ target }: React.FocusEvent<HTMLInputElement>) => {
          if (target.value === "") {
            await setValue(DEFAULT_DATE, true);
            target.setSelectionRange(0, 0);

            setTimeout(() => {
              target.setSelectionRange(0, 0);
            }, 0);
          }
        }}
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        onPaste={handlePaste}
        placeholder={DATE_PLACEHOLDER}
        required={required}
      />
      {errorMessage}
    </>
  );
};

const NativeDatePicker = ({ formField, label, infoIconText, autocomplete, required = false }: Props) => {
  const { fieldName } = formField;
  const [fieldProps, { touched, error }] = useField<string>(fieldName);

  const errorMessage = touched && error ? <ErrorMessage id={fieldName}>{error}</ErrorMessage> : null;

  return (
    <>
      <Label htmlFor={fieldName} text={label} infoIconText={infoIconText} />
      <BronsonInput
        aria-describedby={`error-${fieldName}`}
        id={fieldName}
        error={!!errorMessage}
        autoComplete={autocomplete || "off"}
        type="date"
        {...fieldProps}
        placeholder={DATE_PLACEHOLDER}
        required={required}
      />
      {errorMessage}
    </>
  );
};

export const DatePicker = (props: Props) => (isTouchDevice() ? <NativeDatePicker {...props} /> : <TextDatePicker {...props} />);
